import { config } from '../ProsemirrorConfig';
import { CONTENT_ASSISTANT_INLINE_MENU, CONTENT_ASSISTANT_SLASH_COMMAND, INLINE_IMAGE, LINK, PASTE_OVERRIDE, PERSONALIZATION_TOKEN_INLINE, TRACKING } from '../editorCapabilities/constants';

/** Configuration for analytics tracking callbacks */

import { DropPlugin } from '../plugins/DropPlugin';
import { FocusPlugin } from '../plugins/FocusPlugin';
import { createFontPlugin } from '../plugins/FontPlugin';
import { ImageFocusPlugin } from '../plugins/ImageFocusPlugin';
import { createImagePastePlugin } from '../plugins/ImagePastePlugin';
import { PasteOverridePlugin } from '../plugins/PasteOverridePlugin';
import { ScrollViewPlugin } from '../plugins/ScrollViewPlugin';
import { LinkPlugin } from '../plugins/link/LinkPlugin';
import { createEmojiPlugin } from '../plugins/suggestion/EmojiPlugin';
import { getDefaultFontProps } from './fontUtils';
import { getContentAssistantHighlightPlugin } from '../plugins/ai-highlight/CAHighlightPlugin';
import { SELECTION_COMMANDS, SLASH_COMMANDS } from '../plugins/ai-highlight/commands';
import { createContentAssistantSlashPlugin } from '../plugins/ai-slash/CASlashPlugin';
import { createPersonalizationTokenInlinePlugin } from '../plugins/personalization-token-inline';
import { getSelectedCommandParamsForSlash, onInsertSlashContent } from '../plugins/ai-slash/utils';
import { getGlobalVars } from './globalVarUtil';
import { createAutoDetectLinkPlugin } from '../plugins/link/AutoDetectLinkPlugin';
export function getLinkifyOptions(linkConfig) {
  if (!(linkConfig !== null && linkConfig !== void 0 && linkConfig.autoDetectLinks) && !(linkConfig !== null && linkConfig !== void 0 && linkConfig.autoLinkOnPaste)) {
    return undefined;
  }
  return {
    autoDetectBareUrls: Boolean(linkConfig.autoDetectBareUrls)
  };
}
export const getTracking = editorConfig => {
  return editorConfig[TRACKING] || {};
};
export const getKeyMapOptions = editorConfig => {
  const globalVars = getGlobalVars(editorConfig);
  return Object.assign({}, editorConfig.KEYMAP, {
    usePlaintextMode: !!globalVars.usePlaintextMode,
    plaintextOptions: globalVars.plaintextOptions
  });
};
export const capabilitiesThatArePlugins = [CONTENT_ASSISTANT_INLINE_MENU, CONTENT_ASSISTANT_SLASH_COMMAND, PERSONALIZATION_TOKEN_INLINE];
export function buildDefaultRTEConfig(customPlugins, editorConfig, schema, portalApi) {
  const linkConfig = editorConfig && editorConfig[LINK];
  const inlineImageConfig = editorConfig && editorConfig[INLINE_IMAGE];
  const hasPasteOverrides = editorConfig && editorConfig[PASTE_OVERRIDE];
  const linkifyOptions = getLinkifyOptions(linkConfig);
  const plugins = [LinkPlugin, ScrollViewPlugin, DropPlugin, FocusPlugin, ImageFocusPlugin];
  if (inlineImageConfig) {
    const {
      fileManagerAccess,
      folderPath,
      defaultImageWidth
    } = inlineImageConfig;
    plugins.unshift(createImagePastePlugin({
      fileManagerAccess,
      folderPath,
      defaultImageWidth
    }));
  }
  if (hasPasteOverrides) {
    plugins.push(PasteOverridePlugin);
  }
  if (editorConfig && editorConfig.EMOJI) {
    plugins.push(createEmojiPlugin(editorConfig.EMOJI));
  }
  if (editorConfig) {
    plugins.push(createFontPlugin(getDefaultFontProps(editorConfig)));
  }
  if (linkifyOptions) {
    plugins.push(createAutoDetectLinkPlugin(linkifyOptions));
  }
  if (customPlugins) {
    plugins.push(...customPlugins);
  }
  if (portalApi && editorConfig && editorConfig.CONTENT_ASSISTANT_INLINE_MENU) {
    plugins.push(getContentAssistantHighlightPlugin(Object.assign({
      portalApi,
      usageDescription: 'prosemirror-content-assistant-completion',
      selectionCommands: SELECTION_COMMANDS,
      trackInteraction: () => {}
    }, editorConfig.CONTENT_ASSISTANT_INLINE_MENU)));
  }
  if (portalApi && editorConfig && editorConfig.CONTENT_ASSISTANT_SLASH_COMMAND) {
    plugins.push(createContentAssistantSlashPlugin(Object.assign({
      portalApi,
      commands: SLASH_COMMANDS,
      trackInteraction: () => {},
      usageDescription: 'prosemirror-content-assistant-slash',
      onInsert: onInsertSlashContent,
      getSelectedCommandParams: getSelectedCommandParamsForSlash
    }, editorConfig.CONTENT_ASSISTANT_SLASH_COMMAND)));
  }
  if (portalApi && editorConfig && editorConfig[PERSONALIZATION_TOKEN_INLINE]) {
    const inlineTokenConfig = editorConfig[PERSONALIZATION_TOKEN_INLINE];
    const {
      plugin
    } = createPersonalizationTokenInlinePlugin({
      portalApi,
      fetchTokens: inlineTokenConfig.fetchTokens,
      onSelectToken: inlineTokenConfig.onSelectToken,
      defaultValue: inlineTokenConfig.defaultValue,
      renderCustomFields: inlineTokenConfig.renderCustomFields,
      hasCustomFields: inlineTokenConfig.hasCustomFields
    });
    plugins.push(plugin);
  }
  return config(plugins, editorConfig && getKeyMapOptions(editorConfig), schema);
}